import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
    FaFileAlt, FaCloudUploadAlt, FaTrash, FaInfoCircle, FaTimes,
    FaUser, FaFolderOpen, FaChartLine, FaMoneyBillAlt, 
    FaHandsHelping, FaImage, FaVideo, FaEdit, FaSave, FaListAlt,
    FaSignOutAlt, FaHistory, FaDownload, FaEraser, FaPray, FaKey, FaUnlockAlt, FaSpinner
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import defaultMissionImage from './assets/mission.jpeg'; 

import { LoginScreen, useAuth } from './security';

const API_URL = '/api'; 

const getDefaultUserImage = (userId) => {
    const personNumber = userId - 2;
    const paddedId = personNumber.toString().padStart(2, '0');
    return `/assets/images${paddedId}.jpeg`; 
};

const ALL_CATEGORIES = ['Monthly Report', 'Financial Report', 'Prayer Request', 'Photos', 'Videos'];
const CATEGORY_ICONS = {
    'Monthly Report': FaChartLine, 'Financial Report': FaMoneyBillAlt, 'Prayer Request': FaHandsHelping, 'Photos': FaImage, 'Videos': FaVideo,
};

function App() {
    const { isLoggedIn, authUsersList, isAdmin, isMember, loggedInUser, login, logout, refreshAuthData, isLoading } = useAuth(API_URL);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [userFiles, setUserFiles] = useState([]); 
    const [showModal, setShowModal] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [currentFileInfo, setCurrentFileInfo] = useState(null);
    const [activeTab, setActiveTab] = useState('info'); 
    const [selectedPersonDetail, setSelectedPersonDetail] = useState(null); 
    const [isEditing, setIsEditing] = useState(false); 
    const [editingPersonData, setEditingPersonData] = useState(null); 
    const [activityLogs, setActivityLogs] = useState([]);
    
    const [showCredoModal, setShowCredoModal] = useState(false);
    const displayCategories = isMember ? ALL_CATEGORIES.filter(c => c !== 'Financial Report') : ALL_CATEGORIES;
    const [theme, setTheme] = useState(localStorage.getItem('app-theme') || 'light');

    const [isMuted, setIsMuted] = useState(true);
    const audioRef = useRef(null);

    const [musicList, setMusicList] = useState([]);
    const [currentTrack, setCurrentTrack] = useState('');

    // --- 신규 추가된 상태값 (메시지 관련) ---
    const [siteMessage, setSiteMessage] = useState("");
    const [msgUpdatedAt, setMsgUpdatedAt] = useState(0);
    const [isNewMsg, setIsNewMsg] = useState(false);
    const [showMsgPopup, setShowMsgPopup] = useState(false);
    const [editMsg, setEditMsg] = useState("");

    // --- 공지 메시지 로드 함수 ---
    const fetchSiteMessage = async () => {
        try {
            const res = await axios.get(`${API_URL}/message`);
            const { message, updatedAt } = res.data;
            setSiteMessage(message);
            setEditMsg(message);
            setMsgUpdatedAt(updatedAt);

            const lastRead = localStorage.getItem('lastReadMsgTime');
            if (!lastRead || parseFloat(lastRead) < updatedAt) {
                setIsNewMsg(true);
            }
        } catch (e) {
            console.error("메시지 로드 실패:", e);
        }
    };

    useEffect(() => {
        fetchSiteMessage();
    }, []);

    const toggleMsgPopup = () => {
        setShowMsgPopup(!showMsgPopup);
        if (!showMsgPopup) {
            setIsNewMsg(false);
            localStorage.setItem('lastReadMsgTime', msgUpdatedAt.toString());
        }
    };

    const handleSaveSiteMessage = async () => {
        try {
            await axios.post(`${API_URL}/message`, { message: editMsg, role: 'admin' });
            toast.success("메시지가 업데이트되었습니다.");
            fetchSiteMessage();
        } catch (e) {
            toast.error("메시지 저장 권한이 없습니다.");
        }
    };

    useEffect(() => {
        const loadMusic = async () => {
            try {
                const res = await axios.get('/api/music-list');
                if (res.data && res.data.length > 0) {
                    setMusicList(res.data);
                    const firstRandom = res.data[Math.floor(Math.random() * res.data.length)];
                    setCurrentTrack(firstRandom);
                }
            } catch (e) {
                console.error(e);
            }
        };
        loadMusic();
    }, []);

    const playNextRandomTrack = () => {
        if (musicList.length > 1) {
            let nextTrack;
            do {
                nextTrack = musicList[Math.floor(Math.random() * musicList.length)];
            } while (nextTrack === currentTrack);
            setCurrentTrack(nextTrack);
        }
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !currentTrack) return;

        audio.muted = isMuted;

        const playAudio = () => {
            if (!isMuted && currentTrack) {
                audio.volume = 0.5;
                audio.play()
                    .then(() => {
                        removeEvents();
                    })
                    .catch(err => console.log("DEBUG: Playback blocked", err.message));
            }
        };

        const removeEvents = () => {
            window.removeEventListener('click', playAudio);
            window.removeEventListener('touchstart', playAudio);
            window.removeEventListener('keydown', playAudio);
        };

        playAudio();

        window.addEventListener('click', playAudio);
        window.addEventListener('touchstart', playAudio);
        window.addEventListener('keydown', playAudio);

        return () => removeEvents();
    }, [isMuted, currentTrack]);

        
    useEffect(() => {
        if (isLoggedIn && authUsersList.length > 0) {
            if (selectedUserId !== null) return;
            const savedId = localStorage.getItem('lastSelectedUserId');
            const targetId = savedId ? parseInt(savedId, 10) : null;
            const isValidUser = targetId && authUsersList.find(u => u.id === targetId);
            if (isValidUser) {
                setSelectedUserId(targetId);
            } else {
                setSelectedUserId(authUsersList[0].id);
            }
        } else {
             if (!isLoggedIn && selectedUserId !== null) {
                setSelectedUserId(null);
             }
        }
    }, [isLoggedIn, authUsersList, selectedUserId]);

    useEffect(() => {
        if (selectedUserId) {
            localStorage.setItem('lastSelectedUserId', selectedUserId);
        }
    }, [selectedUserId]);

    useEffect(() => {
        let isMounted = true; 
        const loadData = async () => {
            if (selectedUserId !== null) {
                try {
                    const detailRes = await axios.get(`${API_URL}/person/${selectedUserId}`);
                    if (isMounted) {
                        setSelectedPersonDetail(detailRes.data);
                        setEditingPersonData(detailRes.data ? { ...detailRes.data, password: '' } : null);
                    }
                    const queryParam = isMember ? '?mode=public' : '';
                    const filesRes = await axios.get(`${API_URL}/users/${selectedUserId}/files${queryParam}`);
                    if (isMounted) {
                        setUserFiles(filesRes.data);
                    }
                    if (isMounted) {
                        setActiveTab('info');
                        setIsEditing(false); 
                    }
                } catch (error) {
                    if (isMounted) {
                        setSelectedPersonDetail(null);
                        setUserFiles([]);
                    }
                }
            } else {
                setSelectedPersonDetail(null);
                setUserFiles([]);
            }
        };
        loadData();
        return () => { isMounted = false; };
    }, [selectedUserId, isMember]);


    const filteredFiles = activeTab === 'info' 
        ? [] 
        : userFiles.filter(file => file.category === activeTab);

    useEffect(() => {
        if (activeTab !== 'info' && isEditing) setIsEditing(false);
    }, [activeTab, isEditing]);

    const selectedUserObj = authUsersList.find(u => u.id === selectedUserId);

    const handleAdminResetPassword = async () => {
        if (!isAdmin || !selectedUserId) return;
        const targetUserId = selectedUserId; 
        const newPassword = window.prompt(`[${selectedUserObj?.name}] 선교사님의 새 비밀번호를 입력하세요:`);
        if (!newPassword) return;
        const adminPassword = window.prompt("확인을 위해 관리자(admin) 비밀번호를 입력하세요:");
        if (!adminPassword) return;
        try {
            await axios.post(`${API_URL}/admin/reset-password`, {
                adminPassword: adminPassword,
                targetUserId: targetUserId,
                newPassword: newPassword
            });
            toast.success(`비밀번호가 성공적으로 변경되었습니다.`);
            sendLog('RESET_PW', 'Password Reset by Admin', targetUserId);
        } catch (error) {
            toast.error(error.response?.data?.error || "초기화에 실패했습니다.");
        }
    };

    const credoInfo = () => {
        if (isMember) {
            setShowCredoModal(true);
        } else {
            toast.warn("이 기능은 교회 성도(Member)만 사용할 수 있습니다.");
        }
    };

    const sendLog = async (action, filename, targetUserId) => {
        let actorName = "Unknown";
        if (isAdmin) actorName = "Administrator";
        else if (isMember) actorName = "Church Member";
        else if (loggedInUser) actorName = loggedInUser.name; 
        try {
            await axios.post(`${API_URL}/logs`, { 
                action, 
                filename, 
                actor: actorName, 
                targetUserId: targetUserId || selectedUserId 
            });
        } catch (e) { console.error("Logging Error:", e); }
    };

    const fetchLogs = async () => {
        try {
            const res = await axios.get(`${API_URL}/logs`);
            setActivityLogs(res.data);
            setShowLogModal(true);
        } catch (error) { toast.error("로그 로드 실패"); }
    };

    const handleDownloadLogsCsv = () => {
        if (activityLogs.length === 0) return toast.info("로그 데이터가 존재하지 않습니다.");
        const header = "ID,Timestamp,Actor,Action,Target User ID,Filename\n";
        const rows = activityLogs.map(log => `${log.id},"${log.timestamp}","${log.actor_name}","${log.action}",${log.target_user_id},"${log.filename}"`).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(header + rows));
        link.setAttribute("download", `logs_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    const handleClearLogs = async () => {
        const password = window.prompt("시스템 로그를 전체 삭제하시겠습니까? 관리자 비번을 입력하세요:");
        if (!password) return;
        try {
            await axios.delete(`${API_URL}/logs`, { data: { password } });
            toast.success("로그 데이터가 성공적으로 삭제되었습니다.");
            setActivityLogs([]); 
        } catch (error) { 
            const msg = error.response?.data?.error || "비밀번호가 일치하지 않거나 서버 오류가 발생했습니다.";
            toast.error(msg); 
        }
    };

    const handleFileUpload = async (e) => {
        if (isMember) return;
        const uploadTargetId = selectedUserId;
        const uploadCategory = activeTab;
        if (!uploadTargetId) {
            toast.error("대상이 선택되지 않았습니다.");
            return;
        }
        const file = e.target.files[0];
        if (!file) return; 
        if (!ALL_CATEGORIES.includes(uploadCategory)) {
            toast.warn("Info 탭에서는 업로드할 수 없습니다. 카테고리를 선택하세요.");
            return;
        }
        const targetUser = authUsersList.find(u => u.id === uploadTargetId);
        const formData = new FormData();
        formData.append('userId', uploadTargetId); 
        formData.append('category', uploadCategory);
        formData.append('file', file);
        const toastId = toast.loading(`[${targetUser?.name}] 선교사님 파일 업로드 중...`);
        try {
            const response = await axios.post(
                `${API_URL}/upload?category=${encodeURIComponent(uploadCategory)}&userId=${uploadTargetId}`, 
                formData
            );
            const savedFile = response.data.file;
            if (savedFile && Number(savedFile.user_id) !== Number(uploadTargetId)) {
                toast.update(toastId, { render: "⚠️ 경고: 데이터 저장 위치가 일치하지 않을 수 있습니다.", type: "warning", isLoading: false, autoClose: 2000 });
            } else {
                await sendLog('UPLOAD', file.name, uploadTargetId);
                toast.update(toastId, { render: "파일 업로드 완료!", type: "success", isLoading: false, autoClose: 1000 });
            }
            if (selectedUserId === uploadTargetId) {
                const q = isMember ? '?mode=public' : '';
                const refreshRes = await axios.get(`${API_URL}/users/${uploadTargetId}/files${q}`);
                setUserFiles(refreshRes.data);
                await refreshAuthData(); 
            }
            e.target.value = null; 
        } catch (error) {
            const errMsg = error.response?.data?.error || "업로드 실패";
            toast.update(toastId, { render: errMsg, type: "error", isLoading: false, autoClose: 1000 });
        }
    };

    const handleDeleteFile = async (e, fileId) => {
        if (isMember) return;
        e.stopPropagation();
        const currentSelectedId = selectedUserId;
        const targetFile = userFiles.find(f => f.id === fileId);
        const fileName = targetFile ? targetFile.originalname : "Unknown";
        const password = window.prompt(`[${fileName}] 파일을 삭제하시겠습니까?\n관리자(admin) 비밀번호를 입력하세요:`);
        if (!password) return;
        try {
            await axios.delete(`${API_URL}/files/${fileId}`, { 
                data: { password: password, category: activeTab } 
            });
            await sendLog('DELETE', fileName, currentSelectedId);
            toast.success("파일이 안전하게 삭제되었습니다.");
            if (selectedUserId === currentSelectedId) {
                const q = isMember ? '?mode=public' : '';
                const refreshRes = await axios.get(`${API_URL}/users/${currentSelectedId}/files${q}`);
                setUserFiles(refreshRes.data);
                await refreshAuthData(); 
            }
        } catch (error) { 
            const errorMsg = error.response?.data?.error || "비밀번호가 올바르지 않습니다.";
            toast.error(errorMsg); 
        }
    };

    const handleDownloadTracking = (file) => sendLog('DOWNLOAD', file.originalname, selectedUserId);

    const handleSavePersonalInfo = async () => {
        if (isMember || !selectedUserId) return;
        const targetId = selectedUserId;
        try {
            await axios.patch(`${API_URL}/person/${targetId}`, editingPersonData);
            const detailRes = await axios.get(`${API_URL}/person/${targetId}`);
            if (selectedUserId === targetId) {
                setSelectedPersonDetail(detailRes.data);
            }
            await refreshAuthData();
            setIsEditing(false);
            toast.success("수정 사항이 저장되었습니다.");
        } catch (error) {
            toast.error("정보 저장 실패");
        }
    };
    
    const handleEditChange = (key, value) => setEditingPersonData(p => ({ ...p, [key]: value }));
    
    const handleMarkAsRead = async (fileId) => { 
        const currentId = selectedUserId;
        try { 
            await axios.patch(`${API_URL}/files/${fileId}/read`, { category: activeTab }); 
            const q = isMember ? '?mode=public' : '';
            const res = await axios.get(`${API_URL}/users/${currentId}/files${q}`);
            if (selectedUserId === currentId) setUserFiles(res.data);
            await refreshAuthData(); 
        } catch (e) { console.error("읽음 표시 오류:", e); } 
    };

    const openInfoModal = (e, file) => { e.stopPropagation(); setCurrentFileInfo(file); setShowModal(true); };

    const serverCategoryCount = (selectedUserObj?.counts?.[activeTab] || 0);
    const lastUploadedFile = filteredFiles.length > 0 ? filteredFiles[0] : null;

    const getProfileImage = (userObj, personDetail) => {
        if (personDetail && personDetail.picture) return personDetail.picture; 
        if (!userObj) return null;
        const personNumber = userObj.id - 2;
        const paddedId = personNumber.toString().padStart(2, '0');
        return `/assets/photos${paddedId}.jpeg`
    };

    const handleProfilePhotoUpload = async (e) => {
        if (!selectedUserId || !e.target.files[0]) return;
        const targetId = selectedUserId;
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);
        const toastId = toast.loading("프로필 사진 갱신 중...");
        try {
            const res = await axios.post(`${API_URL}/person/${targetId}/photo`, formData);
            if (res.data.picture && selectedUserId === targetId) {
                setSelectedPersonDetail(prev => ({ ...prev, picture: res.data.picture }));
                if (isEditing) setEditingPersonData(prev => ({ ...prev, picture: res.data.picture }));
            }
            await refreshAuthData(); 
            toast.update(toastId, { render: "프로필 사진이 변경되었습니다.", type: "success", isLoading: false, autoClose: 1000 });
        } catch (error) {
            toast.update(toastId, { render: "이미지 변경 실패", type: "error", isLoading: false, autoClose: 1000 });
        }
    };
    
    return (
        <>
            <ToastContainer position="top-right" autoClose={1000} theme={theme} />

            <audio 
                ref={audioRef} 
                src={currentTrack ? `/music/${encodeURIComponent(currentTrack)}` : ''} 
                muted={isMuted} 
                onEnded={playNextRandomTrack} 
                playsInline
            />

            {isLoading ? (
                <div className="loading-wrapper">
                    <FaSpinner className="loading-icon" />
                    <h3 className="loading-text">Connecting to Mission Portal</h3>
                </div>
            ) : !isLoggedIn ? (
                <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
                    <LoginScreen onLogin={login} isLoading={isLoading} theme={theme} setTheme={setTheme} isMuted={isMuted} setIsMuted={setIsMuted} currentTrack={currentTrack} />
                </div>
            ) : (
                <div className="container">
                    <div className="sidebar">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '15px', paddingTop: '15px' }}>
                            <button onClick={logout} className="logout-btn" title="Logout">
                                <FaSignOutAlt />
                                <span className="logout-text">Logout</span>
                            </button>
                            {isAdmin && (
                                <button onClick={fetchLogs} style={{ background: '#34495e', border: 'none', color: '#ecf0f1', cursor: 'pointer', fontSize: '0.9rem', padding: '6px 12px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
                                    <FaHistory /> logs
                                </button>
                            )}
                            {isMember && (
                                <button onClick={credoInfo} style={{ background: '#45b39d', border: 'none', color: 'white', cursor: 'pointer', fontSize: '0.9rem', padding: '6px 12px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
                                    <FaPray /> Credo
                                </button>
                            )}
                            <h3 style={{ margin: '10px 0 0 0' }}>MISSIONARIES</h3>
                        </div>
                        <div className="user-list">
                            {authUsersList.map((user) => (
                                <div key={user.id} className={`user-item ${selectedUserId === user.id ? 'active' : ''}`} onClick={() => setSelectedUserId(user.id)}>
                                    <div className="icon-wrapper">
                                        <img src={getDefaultUserImage(user.id)} alt={user.name} className="user-avatar" />
                                        {(() => {
                                            if (isMember && user.counts) {
                                                const finCount = user.counts['Financial Report'] || 0;
                                                const totalCountResult = user.total_file_count - finCount;
                                                if (totalCountResult > 0) return <span className="badge">{totalCountResult}</span>;
                                            }
                                            if (!isMember && user.total_file_count > 0) return <span className="badge">{user.total_file_count}</span>;
                                            return null;
                                        })()}
                                    </div>
                                    <span className="user-name">{user.mokjang || user.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="main-content">
                        {/* --- 관리자 메시지 편집 창 --- */}
                        {isAdmin && (
                            <div className="site-message-editor">
                                <h4><FaEdit /> 전체 공지사항 편집 (관리자 전용)</h4>
                                <textarea 
                                    value={editMsg} 
                                    onChange={(e) => setEditMsg(e.target.value)}
                                    placeholder="로그인 전 화면에 표시될 메시지를 입력하세요."
                                />
                                <button onClick={handleSaveSiteMessage}><FaSave /> 공지 업데이트</button>
                            </div>
                        )}

                        {selectedUserObj ? (
                            <div className="content-wrapper">
                                <div className="tab-header-wrapper">
                                    <div className="tab-header">
                                        <button className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')} title="Personal Info"><FaUser size={18} /></button>
                                        {displayCategories.map((category) => {
                                            const Icon = CATEGORY_ICONS[category] || FaFolderOpen;
                                            return (
                                                <button key={category} className={`tab-btn category-tab ${activeTab === category ? 'active' : ''}`} onClick={() => setActiveTab(category)} title={category}><Icon size={18} /></button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {activeTab === 'info' && selectedPersonDetail && (
                                    <div className="tab-content personal-info-content">
                                        <div className="person-header">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <h2>{selectedUserObj?.name}</h2>
                                                    {isAdmin && (
                                                        <button onClick={handleAdminResetPassword} className="reset-pw-btn"><FaUnlockAlt /> Reset PW</button>
                                                    )}
                                                </div>
                                                {!isMember && (
                                                    <span className="info-edit-controls" style={{ alignSelf: 'flex-start' }}>
                                                        {isEditing ? (
                                                            <>
                                                                <button onClick={handleSavePersonalInfo} className="save-btn"><FaSave size={14} /> Save</button>
                                                                <button onClick={() => setIsEditing(false)} className="cancel-btn"><FaTimes size={14} /> Cancel</button>
                                                            </>
                                                        ) : (
                                                            <button onClick={() => setIsEditing(true)} className="edit-btn"><FaEdit size={14} /> Edit</button>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ position: 'relative', marginLeft: 'auto' }}>
                                                <img src={getProfileImage(selectedUserObj, selectedPersonDetail)} alt={selectedUserObj?.name || 'User'} className="header-person-image" />
                                                {isEditing && (
                                                    <label htmlFor="profile-upload" style={{ position: 'absolute', bottom: -5, right: -5, backgroundColor: '#3498db', color: 'white', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }} title="사진 변경">
                                                        <FaCloudUploadAlt size={18} />
                                                        <input id="profile-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfilePhotoUpload} />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                        <div className="person-detail-card">
                                            <div className="info-grid">
                                                <div className="info-item"><strong>Name</strong> {(!isMember && isEditing) ? <input type="text" value={editingPersonData?.name || ''} onChange={(e)=>handleEditChange('name', e.target.value)} /> : <span>{selectedPersonDetail.name}</span>}</div>      
                                                <div className="info-item"><strong>Missionary Type</strong> {(!isMember && isEditing) ? <select value={editingPersonData?.missionary || ''} onChange={(e)=>handleEditChange('missionary', e.target.value)}><option value="Supported Missionary">Supported Missionary</option><option value="Self-funded Missionary">Self-funded Missionary</option><option value="S Missionnary">S Missionnary</option></select> : <span>{selectedPersonDetail.missionary}</span>}</div>
                                                <div className="info-item"><strong>Phone</strong> {isMember ? <span style={{color:'#95a5a6'}}>(비공개)</span> : (isEditing ? <input type="text" value={editingPersonData?.phone || ''} onChange={(e)=>handleEditChange('phone', e.target.value)} /> : <span>{selectedPersonDetail.phone}</span>)}</div>
                                                <div className="info-item"><strong>Email</strong> {(!isMember && isEditing) ? <input type="text" value={editingPersonData?.email || ''} onChange={(e)=>handleEditChange('email', e.target.value)} /> : <span>{selectedPersonDetail.email}</span>}</div>
                                                <div className="info-item"><strong>Country</strong> {(!isMember && isEditing) ? <input type="text" value={editingPersonData?.country || ''} onChange={(e)=>handleEditChange('country', e.target.value)} /> : <span>{selectedPersonDetail.country}</span>}</div>
                                                <div className="info-item"><strong>Support Account</strong> {isMember ? <span style={{color:'#95a5a6'}}>(교회 사무실 문의)</span> : (isEditing ? <input type="text" value={editingPersonData?.account || ''} onChange={(e)=>handleEditChange('account', e.target.value)} /> : <span>{selectedPersonDetail.account}</span>)}</div>
                                            </div>
                                            <div>
                                                {!isMember && !isAdmin && isEditing && (
                                                    <div className="info-item" style={{ padding: '12px', borderRadius: '8px', background: 'var(--card-s-bg)', border: '1px solid var(--border-color)', marginTop: '10px' }}>
                                                        <strong style={{ color: 'var(--accent-color)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <FaKey size={12}/> Change Password
                                                        </strong>
                                                        <input 
                                                            type="password" 
                                                            placeholder="새 비밀번호 입력" 
                                                            value={editingPersonData?.password || ''} 
                                                            onChange={(e) => handleEditChange('password', e.target.value)} 
                                                            style={{ width: '80%', marginTop: '8px', background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', padding: '5px', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="info-bio"><strong>Bio</strong> {(!isMember && isEditing) ? <textarea value={editingPersonData?.bio || ''} onChange={(e)=>handleEditChange('bio', e.target.value)} rows="4" /> : <p style={{ whiteSpace: 'pre-wrap' }}> {selectedPersonDetail.bio ? selectedPersonDetail.bio.replaceAll('\\n', '\n').replaceAll('&#10;', '\n') : ''} </p> }</div>
                                        </div>
                                    </div>
                                )}


                                {displayCategories.includes(activeTab) && (
                                    <div className="tab-content file-reports-content">
                                        <div className="reports-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', justifyContent: 'space-between' }}>
                                            <h3 style={{ margin: 10, color: 'var(--text-primary)' }}>{activeTab}</h3>
                                            {!isMember && (
                                                <label className="upload-btn">
                                                    <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                                                    <FaCloudUploadAlt className="upload-icon" size={16} style={{ marginRight: '8px' }} /> Upload
                                                </label>
                                            )}
                                        </div>

                                        <div className="summary-box" style={{ 
                                            marginBottom: '20px', 
                                            padding: '12px 15px', 
                                            backgroundColor: 'var(--card-s-bg)', 
                                            borderLeft: '4px solid var(--blue-color)', 
                                            borderRadius: '0 4px 4px 0', 
                                            color: 'var(--text-secondary)', 
                                            fontSize: '0.9rem', 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            gap: '5px' 
                                        }}>
                                            <div className="summary-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                <FaListAlt /> <span>Summary</span>
                                            </div>
                                            <div className="summary-details" style={{ paddingLeft: '24px' }}>
                                                <span>Total Files: <strong style={{ color: 'var(--text-primary)' }}>{serverCategoryCount}</strong></span>
                                                {lastUploadedFile && (
                                                    <>
                                                        <span className="summary-divider" style={{ margin: '0 10px', color: 'var(--border-color)' }}>|</span>
                                                        <span>Last Upload: <strong style={{ color: 'var(--text-primary)' }}>{lastUploadedFile.upload_time}</strong> ({lastUploadedFile.originalname})</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="file-grid">
                                            {filteredFiles.length === 0 ? (
                                                <p className="empty-msg">No {activeTab} files found.</p>
                                            ) : filteredFiles.map((file) => (
                                                <div key={file.id} className={`file-item ${file.is_read === 1 ? 'read' : ''}`} onClick={() => handleMarkAsRead(file.id)}>
                                                    <button className="action-btn left info-btn" onClick={(e) => openInfoModal(e, file)}>
                                                        <FaInfoCircle size={22} color="var(--text-s-muted)" />
                                                    </button>
                                                    {!isMember && (
                                                        <button className="action-btn right delete" onClick={(e) => handleDeleteFile(e, file.id)}>
                                                            <FaTrash size={18} />
                                                        </button>
                                                    )}
                                                    <div className="file-icon-wrapper">
                                                        <a href={encodeURI(`/uploads/${file.filename}`)} download={file.originalname} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); handleDownloadTracking(file); }}>
                                                            <FaFileAlt size={45} color={file.is_read === 1 ? "var(--text-s-muted)" : "var(--blue-color)"} />
                                                        </a>
                                                    </div>
                                                    <div className="file-info">
                                                        <span className="file-name" title={file.originalname} style={{ color: 'var(--text-primary)' }}>{file.originalname}</span>
                                                        <span className="file-time" style={{ color: 'var(--text-s-muted)' }}>{file.upload_time}</span>
                                                        <span className="file-category-tag" style={{ backgroundColor: 'var(--border-light)', color: 'var(--blue-color)' }}>{file.category}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                            </div> 
                        ) : (
                            isLoggedIn ? (
                                <div className="loading-wrapper">
                                    <FaSpinner className="loading-icon" />
                                    <h3 className="loading-text">Connecting to Database</h3>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <img src={defaultMissionImage} alt="Mission Background" className="empty-state-background-image" />
                                    <div className="empty-state-overlay"><h1>MISSIONARY PORTAL</h1><p>선교사님을 선택하여 사역 현황을 확인하세요.</p></div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}
            
            {showModal && currentFileInfo && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content file-details-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                                <FaInfoCircle /> File Details
                            </h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}><FaTimes /></button>
                        </div>

                        <div className="modal-body" style={{ display: 'flex', flexDirection: 'row', padding: '30px 20px', gap: '20px', alignItems: 'flex-start' }}>
                            <div style={{ minWidth: '80px', display: 'flex', justifyContent: 'center', paddingTop: '5px' }}>
                                <FaFileAlt size={70} color="var(--blue-color)" />
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary)', wordBreak: 'break-all', lineHeight: '1.4' }}>
                                    {currentFileInfo.originalname}
                                </span>
                                
                                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    {currentFileInfo.category} 
                                    <span style={{ margin: '0 5px', color: 'var(--border-color)' }}>|</span> 
                                    {currentFileInfo.upload_time}
                                </span>
                                
                                <span style={{ 
                                    fontSize: '0.85rem', 
                                    fontWeight: '700', 
                                    color: currentFileInfo.is_read === 1 ? 'var(--green-color)' : '#e67e22', 
                                    marginTop: '4px' 
                                }}>
                                    {currentFileInfo.is_read === 1 ? "✔ Confirmed" : "• Unconfirmed"}
                                </span>
                                
                                <a 
                                    href={encodeURI(`/uploads/${currentFileInfo.filename}`)} 
                                    download={currentFileInfo.originalname} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    onClick={() => handleDownloadTracking(currentFileInfo)} 
                                    style={{ 
                                        marginTop: '12px', 
                                        color: 'var(--blue-color)', 
                                        fontWeight: 'bold', 
                                        textDecoration: 'none', 
                                        fontSize: '0.9rem',
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    Download File &rarr;
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {showLogModal && (
                <div className="modal-overlay" onClick={() => setShowLogModal(false)}>
                    <div className="modal-content log-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header"><h3><FaHistory /> System Activity Logs</h3><button className="close-btn" onClick={() => setShowLogModal(false)}><FaTimes /></button></div>
                        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '5px' }}>
                                <button onClick={handleDownloadLogsCsv} style={{ padding: '8px 12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}><FaDownload /> CSV</button>
                                <button onClick={handleClearLogs} style={{ padding: '8px 12px', background: '#c0392b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}><FaEraser /> Clear</button>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead><tr style={{ background: '#ecf0f1', borderBottom: '2px solid #bdc3c7' }}><th style={{ padding: '10px', textAlign: 'left' }}>Time</th><th style={{ padding: '10px', textAlign: 'left' }}>Actor</th><th style={{ padding: '10px', textAlign: 'left' }}>Action</th><th style={{ padding: '10px', textAlign: 'left' }}>File</th></tr></thead>
                                <tbody>{activityLogs.map(log => (<tr key={log.id} style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '8px' }}>{log.timestamp}</td><td style={{ padding: '8px', fontWeight: 'bold' }}>{log.actor_name}</td><td style={{ padding: '8px' }}>{log.action}</td><td style={{ padding: '8px' }}>{log.filename}</td></tr>))}</tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showCredoModal && (
                <div className="modal-overlay" onClick={() => setShowCredoModal(false)}>
                    <div className="modal-content credo-modal-content">
                        <div className="modal-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.5)' }}>
                            <h3 style={{ color: 'white', fontSize: '1.2em' }}><FaPray /> Fides, Spes et Caritas</h3>
                            <button className="close-btn" onClick={() => setShowCredoModal(false)} style={{ color: 'white' }}><FaTimes /></button>
                        </div>
                        <div className="modal-body" style={{ marginTop: '20px', lineHeight: '1.6', color: 'white' }}>
                            <h4>We believe in God the Father, Almighty.</h4>
                            <p>We are called to be the light of the world, shining in the mission fields and supporting those who are sent.</p>
                            <p style={{ marginTop: '15px', fontWeight: 'bold' }}>"Go ye therefore, and teach all nations..." (Matthew 28:19)</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

}

export default App;